import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Building2, Mail, Phone, Globe, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLES = {
  in_attesa: "bg-amber-400/15 text-amber-300",
  approvata: "bg-emerald-400/15 text-emerald-300",
  rifiutata: "bg-red-400/15 text-red-300",
};

const STATUS_LABELS = { in_attesa: "In attesa", approvata: "Approvata", rifiutata: "Rifiutata" };

const inputCls =
  "w-full rounded-xl border border-amber-500/25 bg-[#0A1D15]/80 px-4 py-2.5 text-sm text-[#FAF6EE] placeholder:text-emerald-100/40 outline-none focus:border-amber-400/70";

export const ProposalsTab = () => {
  const [proposals, setProposals] = useState(null);
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = () =>
    api
      .get("/admin/brand-proposals")
      .then((r) => {
        setProposals(r.data);
        setNotes(Object.fromEntries(r.data.map((p) => [p.id, p.admin_notes || ""])));
      })
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
  }, []);

  const review = async (id, status) => {
    setBusyId(id);
    try {
      const { data } = await api.put(`/admin/brand-proposals/${id}`, { status, admin_notes: notes[id] || "" });
      if (status === "approvata") {
        toast.success(data.email_sent ? "Proposta approvata: email inviata al brand." : "Proposta approvata, ma l'email non è partita (controlla l'indirizzo).");
      } else {
        toast.success(status === "rifiutata" ? "Proposta rifiutata." : "Proposta rimessa in attesa.");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  if (!proposals) {
    return <div className="h-10 w-10 animate-spin rounded-full border-2 border-amber-400/30 border-t-amber-400" data-testid="proposals-loading" />;
  }

  return (
    <div className="space-y-5" data-testid="proposals-tab">
      <p className="text-sm text-emerald-100/60">
        Le proposte inviate dai brand dal modulo pubblico "Diventa partner". Approvando, il brand
        riceve un'email con le tue note.
      </p>
      {proposals.length === 0 && (
        <div className="rounded-2xl border border-amber-500/20 bg-[#0C241A]/50 p-8 text-center text-sm text-emerald-100/50" data-testid="proposals-empty">
          Nessuna proposta ricevuta finora.
        </div>
      )}
      {proposals.map((p) => (
        <div key={p.id} data-testid={`proposal-card-${p.id}`} className="rounded-2xl border border-amber-500/20 bg-[#0C241A]/50 p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-display text-lg font-bold text-amber-100">{p.company_name}</h3>
              <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-emerald-100/60">
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.city}</span>
                <span className="rounded-full bg-emerald-400/10 px-2 py-0.5">{p.category}</span>
                {p.website && (
                  <a href={p.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-amber-200/80 underline decoration-amber-400/30 hover:text-amber-100" data-testid={`proposal-website-${p.id}`}>
                    <Globe className="h-3 w-3" /> {p.website.replace(/^https?:\/\//, "")}
                  </a>
                )}
              </p>
            </div>
            <span className={`whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLES[p.status] || STATUS_STYLES.in_attesa}`} data-testid={`proposal-status-${p.id}`}>
              {STATUS_LABELS[p.status] || p.status}
            </span>
          </div>

          <div className="mt-4 rounded-xl bg-[#081C13]/70 p-4">
            <p className="text-sm leading-relaxed text-emerald-100/85"><span className="font-semibold text-amber-200/90">Offerta:</span> {p.offer}</p>
            <p className="mt-2 text-xs leading-relaxed text-emerald-100/60">
              {p.normal_value && <>Valore normale: € {p.normal_value} · </>}
              {p.max_vouchers && <>Max voucher: {p.max_vouchers} · </>}
              {p.expires_on && <>Scadenza: {new Date(p.expires_on).toLocaleDateString("it-IT")}</>}
            </p>
            {p.conditions && <p className="mt-2 text-xs leading-relaxed text-emerald-100/60"><span className="font-semibold">Condizioni:</span> {p.conditions}</p>}
          </div>

          <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-emerald-100/60">
            <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> {p.contact_name}</span>
            <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {p.email}</span>
            <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {p.phone}</span>
            <span className="text-emerald-100/40">Ricevuta il {new Date(p.created_at).toLocaleDateString("it-IT")}</span>
          </p>

          <textarea
            rows={2}
            data-testid={`proposal-notes-${p.id}`}
            value={notes[p.id] ?? ""}
            onChange={(e) => setNotes((n) => ({ ...n, [p.id]: e.target.value }))}
            placeholder="Note per il brand (facoltative — incluse nell'email di approvazione)"
            className={`${inputCls} mt-4 resize-none`}
          />
          <div className="mt-3 flex flex-wrap gap-2.5">
            <button
              type="button"
              data-testid={`proposal-approve-${p.id}`}
              disabled={busyId === p.id}
              onClick={() => review(p.id, "approvata")}
              className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 px-4 py-2.5 text-sm font-bold text-emerald-50 transition-transform hover:scale-[1.02] disabled:opacity-60"
            >
              <CheckCircle2 className="h-4 w-4" /> {p.status === "approvata" ? "Riapprova e reinvia email" : "Approva e invia email"}
            </button>
            <button
              type="button"
              data-testid={`proposal-reject-${p.id}`}
              disabled={busyId === p.id}
              onClick={() => review(p.id, "rifiutata")}
              className="inline-flex items-center gap-1.5 rounded-xl border border-red-400/40 bg-[#4A0D16]/60 px-4 py-2.5 text-sm font-semibold text-red-100 transition-colors hover:bg-[#5E121D] disabled:opacity-60"
            >
              <XCircle className="h-4 w-4" /> Rifiuta
            </button>
            {p.status !== "in_attesa" && (
              <button
                type="button"
                data-testid={`proposal-pending-${p.id}`}
                disabled={busyId === p.id}
                onClick={() => review(p.id, "in_attesa")}
                className="rounded-xl border border-amber-500/25 px-4 py-2.5 text-sm text-emerald-100/70 transition-colors hover:text-amber-200 disabled:opacity-60"
              >
                Rimetti in attesa
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
